import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Qué significa que un medicamento esté aprobado «en etiqueta»?",
    a: "Las indicaciones «en etiqueta» son los usos que la FDA aprobó, según el estudio clínico principal del medicamento. Para la semaglutida y la tirzepatida, esos estudios incluyeron un IMC mínimo, así que el requisito de IMC pasó a formar parte de la etiqueta aprobada.",
  },
  {
    q: "¿Puedo recibir el medicamento «fuera de etiqueta»?",
    a: "Sí, si usted paga de su bolsillo. Recetar fuera de etiqueta es legal y una práctica médica habitual. El personal de Virginia Bariatric Surgery tiene margen para apartarse de los criterios en etiqueta, incluido el IMC, caso por caso.",
  },
  {
    q: "¿Qué IMC exigen las aseguradoras en Virginia?",
    a: "La mayoría de los planes comerciales exigen un IMC de 30, o de 27 con una condición relacionada con el peso. Medicaid de Virginia generalmente exige más de 40, o más de 37 con una condición que califique, y los planes de empleados estatales (COVA) subieron su umbral a 35 a partir del 1 de julio de 2026.",
  },
  {
    q: "¿Por qué me negaron la autorización previa?",
    a: "El motivo más frecuente de negación no es el IMC. Casi siempre es documentación incompleta: un intento de cambio de estilo de vida que sí ocurrió pero nunca quedó registrado en el expediente, o una condición que califica pero que no se incluyó en la solicitud.",
  },
  {
    q: "¿Qué pasa cuando se renueva la receta?",
    a: "La mayoría de los planes aprueban un curso inicial de 4 a 12 meses y exigen una pérdida de peso documentada de al menos 5 por ciento —4 por ciento en el caso de Saxenda— antes de renovar. Medicaid de Virginia deja de renovar cuando el IMC baja de 25.",
  },
  {
    q: "¿Y si no cumplo los criterios de mi aseguradora?",
    a: "Tiene otras vías: pagar de su bolsillo (donde no aplican los umbrales de IMC del seguro y los precios directos del fabricante han bajado bastante), calificar por otra indicación como la apnea del sueño o la enfermedad del hígado graso, o presentar una solicitud más completa o una apelación. Muchas negaciones se deben a documentación incompleta, no a que usted no califique de verdad.",
  },
];

export const Route = createFileRoute("/es/quien-califica-para-medicamentos")({
  head: () => {
    const base = esHead("medQualifications");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("medQualifications"),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/quien-califica-para-medicamentos#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/quien-califica-para-medicamentos#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsMedQualifications,
});

function EsMedQualifications() {
  return (
    <EsPageShell
      page="medQualifications"
      title="¿Califico para medicamentos inyectables para bajar de peso?"
      intro="Criterios «en etiqueta», qué pasa si receta fuera de etiqueta, y qué exige realmente cada aseguradora en Virginia. Aquí explicamos, en español, cómo se toma esta decisión en Virginia Bariatric Surgery."
    >
      <EsSection id="en-etiqueta" heading="Recetas «en etiqueta» y «fuera de etiqueta»">
        <p>
          Las recetas se clasifican como «en etiqueta» o «fuera de etiqueta» según la condición
          médica específica —la indicación, en el lenguaje médico— para la que se recetaron. Las
          indicaciones en etiqueta son las que aprobó la FDA, tomadas directamente del estudio
          clínico principal del medicamento, el ensayo especial que sirvió de base para su
          aprobación.
        </p>
        <p>
          Los estudios principales de la semaglutida y la tirzepatida incluyeron, entre otros
          criterios de selección de pacientes, un IMC mínimo. Ese requisito de IMC quedó
          incorporado en las indicaciones aprobadas en etiqueta.
        </p>
        <p>
          Con el tiempo, para la mayoría de los medicamentos se descubre que son útiles más allá de
          las indicaciones en etiqueta. Esos usos adicionales son los «fuera de etiqueta».
          Lamentablemente, las aseguradoras suelen cubrir solo las indicaciones en etiqueta.
        </p>
        <p>
          <strong>
            Si usted depende del seguro médico para cubrir su medicamento, entonces debe cumplir
            los umbrales de IMC en etiqueta, e incluso algunos más estrictos.
          </strong>
        </p>
        <p>
          <strong>Si paga de su bolsillo, no está sujeto a los criterios en etiqueta</strong>,
          incluidos los límites de IMC. El personal de Virginia Bariatric Surgery tiene margen para
          apartarse de esos criterios. La literatura médica está llena de reportes de medicamentos
          para bajar de peso usados de manera segura para múltiples situaciones fuera de etiqueta.
          La semaglutida y la tirzepatida son, en general, bastante seguras y tienen un perfil de
          efectos adversos comparativamente benigno, y pueden recetarse con seguridad en muchas
          circunstancias. La decisión de recetar fuera de etiqueta se toma caso por caso.
        </p>
      </EsSection>

      <EsSection id="criterios-imc" heading="Lo que dice la etiqueta aprobada sobre el IMC">
        <p>
          En términos generales, la aprobación de la FDA para estos medicamentos cubre a adultos
          con un IMC de 30 o más (obesidad), o de 27 o más (sobrepeso) junto con al menos una
          condición relacionada con el peso, como presión alta, diabetes tipo 2, apnea del sueño o
          colesterol elevado. Algunos productos también tienen aprobaciones adicionales, por
          ejemplo para reducir eventos cardiovasculares o para tratar la enfermedad del hígado
          graso con fibrosis.
        </p>
        <p>
          «Obesidad» aquí se define como un IMC de 30 o más. «Sobrepeso» se define como un IMC
          mayor de 25 pero menor de 30. En pacientes de 12 a 17 años, la obesidad se define como un
          IMC igual o mayor al percentil 95 para la edad y el sexo.
        </p>
      </EsSection>

      <EsSection id="aseguradoras" heading="Lo que exigen las aseguradoras en Virginia">
        <p>
          Los criterios exactos varían según el plan y cambian con frecuencia, pero en términos
          generales:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Medicaid de Virginia (Cardinal Care):</strong> IMC mayor de 40, o mayor de 37
            con colesterol alto, presión alta o diabetes tipo 2, además de un intento documentado
            de otro medicamento sin GLP-1 y consejería nutricional.
          </li>
          <li>
            <strong>Planes de empleados estatales (COVA):</strong> IMC de 35 o más a partir del 1
            de julio de 2026.
          </li>
          <li>
            <strong>Planes comerciales</strong> (Anthem, Aetna, Cigna, UnitedHealthcare, Sentara):
            generalmente IMC de 30, o de 27 con una condición que califique, con documentación de
            cambios de estilo de vida previos.
          </li>
          <li>
            <strong>TRICARE:</strong> IMC de 30, o de 27 a 29 con una condición relacionada con el
            peso, más 6 meses de cambios documentados y un intento fallido de un medicamento
            genérico.
          </li>
          <li>
            <strong>Marketplace y planes de grupo pequeño en Virginia:</strong> el plan de
            referencia del estado excluye los medicamentos para bajar de peso, así que estos planes
            generalmente no los cubren con ningún IMC.
          </li>
        </ul>
        <p>
          La <strong>razón más frecuente de una negación no es el IMC</strong>. Es documentación
          incompleta: un intento de cambio de hábitos que ocurrió pero nunca se registró, o una
          condición que califica pero que no se envió con la solicitud. Mientras más completo esté
          su expediente antes de que presentemos el trámite, mejores serán sus probabilidades.
        </p>
        <p>
          Revise los detalles plan por plan en nuestra{" "}
          <Link
            to="/es/cobertura-de-seguro"
            className="font-medium text-brand-logo hover:underline"
          >
            página de cobertura de seguro
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="si-no-califico" heading="Si no cumple los criterios de su aseguradora">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Pago propio.</strong> Sin seguro, los umbrales de IMC de las aseguradoras no
            aplican, y el precio directo del fabricante ha bajado bastante en el último año.
          </li>
          <li>
            <strong>Otra indicación.</strong> Estos medicamentos también están aprobados para la
            apnea del sueño, la reducción de riesgo cardiovascular y la enfermedad del hígado
            graso; a veces es un camino de cobertura más sencillo si tiene una de esas condiciones.
          </li>
          <li>
            <strong>Una solicitud más sólida o una apelación.</strong> Muchas negaciones se deben a
            documentación incompleta, no a que usted no califique de verdad.
          </li>
        </ul>
        <p>
          ¿No está seguro de dónde está parado? Traiga su tarjeta de seguro a su consulta.
          Revisaremos los criterios de su plan, si los cumple, y las opciones si no es así,
          incluido cuánto costaría realmente pagar de su bolsillo.
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="comparar" heading="Más información">
        <p>
          Vea la{" "}
          <Link
            to="/es/medicamentos-para-bajar-de-peso"
            className="font-medium text-brand-logo hover:underline"
          >
            guía completa de medicamentos
          </Link>
          , la{" "}
          <Link to="/es/semaglutida" className="font-medium text-brand-logo hover:underline">
            semaglutida
          </Link>
          , la{" "}
          <Link to="/es/tirzepatida" className="font-medium text-brand-logo hover:underline">
            tirzepatida
          </Link>{" "}
          o converse con nosotros en{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            contacto
          </Link>
          .
        </p>
      </EsSection>
    </EsPageShell>
  );
}
